import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../environments/environment';
import { IUser } from '../models/other/user.interface';

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  private notificationHubConnection: signalR.HubConnection | undefined;
  private templateHubConnection: signalR.HubConnection | undefined;
  private campaignHubConnection: signalR.HubConnection | undefined;

  constructor() {
  }

  public startNotificationConnection(): void {
    const hubUrl = environment.apiUrlNoApiM + 'notificationHub';
    this.notificationHubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: false,
      }) // Adjust with your API URL
      .build();

    this.notificationHubConnection
      .start()
      .then(() => {
        // console.log('SignalR connection established');
      })
      .catch((err) => {
        console.log('Error while establishing connection: ' + err);
      });
  }

  public startTemplateConnection(): void {
    const hubUrl = environment.apiUrlNoApiM + 'templateHub';
    this.templateHubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: false,
      }) // Adjust with your API URL
      .build();

    this.templateHubConnection
      .start()
      .then(() => {
        // console.log('SignalR connection established');
      })
      .catch((err) => {
        console.log('Error while establishing connection: ' + err);
      });
  }

  public startCampaignConnection(): void {
    const hubUrl = environment.apiUrlNoApiM + 'campaignHub';
    this.campaignHubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: false,
      }) // Adjust with your API URL
      .build();

    this.campaignHubConnection
      .start()
      .then(() => {
        // console.log('SignalR connection established');
      })
      .catch((err) => {
        console.log('Error while establishing connection: ' + err);
      });
  }

  public addNotificationListener(
    callback: (notificationId: string, blockId: string) => void
  ): void {
    if (this.notificationHubConnection) {
      this.notificationHubConnection.off('ReceiveNotification');
      this.notificationHubConnection.on(
        'ReceiveNotification',
        (notificationId, blockId) => {
          callback(notificationId, blockId);
        }
      );
    }
  }

  public addReplyListener(
    callback: (
      notificationId: string,
      replyText: string,
      replyId: string,
      userInfo: IUser
    ) => void
  ): void {
    if (this.notificationHubConnection) {
      this.notificationHubConnection.on(
        'ReceiveReply',
        (notificationId, replyText, replyId, userInfo) => {
          callback(notificationId, replyText, replyId, userInfo);
        }
      );
    }
  }

  public addTemplateLockedByListener(
    callback: (templateId: string, lockedBy: IUser) => void
  ): void {
    if (this.notificationHubConnection) {
      this.notificationHubConnection.on(
        'ReceiveTemplateLockedBy',
        (templateId, lockedBy) => {
          callback(templateId, lockedBy);
        }
      );
    }
  }

  public addTemplateProgressListener(
    callback: (templateName: string, isBeingProc: boolean) => void
  ): void {
    if (this.templateHubConnection) {
      this.templateHubConnection.on(
        'TemplateProgress',
        (templateName, isBeingProc) => {
          callback(templateName, isBeingProc);
        }
      );
    }
  }

  public addTemplateDeletionListener(
    callback: (templateName: string, isBeingProc: boolean) => void
  ): void {
    if (this.templateHubConnection) {
      this.templateHubConnection.on(
        'TemplateDeleted',
        (templateName, isDeleted) => {
          callback(templateName, isDeleted);
        }
      );
    }
  }

  public campaignDeletionListener(
    callback: (campaignName: string, isDeleted: boolean) => void
  ): void {
    if (this.campaignHubConnection) {
      this.campaignHubConnection.off('CampaignDeleted');
      this.campaignHubConnection.on(
        'CampaignDeleted',
        (campaignName, isDeleted) => {
          callback(campaignName, isDeleted);
        }
      );
    }
  }

  /**
   * Subscribes to the events the v2 review panel needs. Handlers are removed by reference in the returned
   * dispose function, so listeners registered elsewhere (e.g. the v1 comment component) are left alone.
   */
  public onReviewEvents(handlers: {
    commentsChanged: (notificationId: string, blockId?: string) => void;
    versionsChanged: (templateId: string, language: string) => void;
  }): () => void {
    const onNotification = (notificationId: string, blockId?: string) => handlers.commentsChanged(notificationId, blockId);
    const onReply = (notificationId: string) => handlers.commentsChanged(notificationId);
    const onVersions = (templateId: string, language: string) => handlers.versionsChanged(templateId, language);

    this.notificationHubConnection?.on('ReceiveNotification', onNotification);
    this.notificationHubConnection?.on('ReceiveReply', onReply);
    this.templateHubConnection?.on('TemplateVersionsChanged', onVersions);

    return () => {
      this.notificationHubConnection?.off('ReceiveNotification', onNotification);
      this.notificationHubConnection?.off('ReceiveReply', onReply);
      this.templateHubConnection?.off('TemplateVersionsChanged', onVersions);
    };
  }
}
